import { useState, useMemo } from 'react'
import { AudioAsset } from 'types'
import type { AudioTrack, MediaObject } from 'types'

type Step = 'library' | 'timeline'

const getExistingMusicTrack = (mediaObject: MediaObject) => {
  return mediaObject.audio?.tracks?.find((track) => track.type === 'music') ?? null
}

const hasOriginalAudioTrack = (mediaObject: MediaObject) => {
  return mediaObject.audio?.tracks?.some((track) => track.type === 'original') ?? false
}

const convertTrackToStockAudioItem = (track: AudioTrack): AudioAsset => {
  return {
    id: track.provider_id || track.media_file_key,
    title: track.title || '',
    duration: track.duration,
    preview_url: track.url,
    thumbnail_url: track.thumbnail_url,
    type: 'music',
    media_file_key: track.media_file_key,
  }
}

export const useAudioEditorState = (mediaObject: MediaObject) => {
  const audioConfig = useMemo(() => {
    const existingMusicTrack = getExistingMusicTrack(mediaObject)
    const hasOriginalAudio = hasOriginalAudioTrack(mediaObject)

    return {
      existingMusicTrack,
      hasOriginalAudio,
      initialStep: existingMusicTrack ? ('timeline' as Step) : ('library' as Step),
      initialAudio: existingMusicTrack ? convertTrackToStockAudioItem(existingMusicTrack) : null,
      initialStartTime: existingMusicTrack?.start_time ?? 0,
      initialMuteState: !hasOriginalAudio,
    }
  }, [mediaObject])

  const [step, setStep] = useState<Step>(audioConfig.initialStep)
  const [selectedAudio, setSelectedAudio] = useState<AudioAsset | null>(audioConfig.initialAudio)
  const [musicStartTime, setMusicStartTime] = useState(audioConfig.initialStartTime)
  const [isVideoMuted, setIsVideoMuted] = useState(audioConfig.initialMuteState)

  const handleSelectAudio = (audio: AudioAsset) => {
    setSelectedAudio(audio)
    setMusicStartTime(0)
    setStep('timeline')
  }

  const handleReplaceAudio = () => {
    setStep('library')
  }

  const handleClearAudio = () => {
    setSelectedAudio(null)
  }

  const handleToggleVideoMute = () => {
    setIsVideoMuted((prev) => !prev)
  }

  const hasChanges = useMemo(() => {
    const audioChanged = selectedAudio?.id !== audioConfig.initialAudio?.id
    const startTimeChanged = musicStartTime !== audioConfig.initialStartTime
    const muteStateChanged = isVideoMuted !== audioConfig.initialMuteState

    return audioChanged || startTimeChanged || muteStateChanged
  }, [selectedAudio, musicStartTime, isVideoMuted, audioConfig])

  return {
    hasOriginalAudio: audioConfig.hasOriginalAudio,
    hasExistingMusicTrack: !!audioConfig.existingMusicTrack,
    hasChanges,
    step,
    selectedAudio,
    musicStartTime,
    isVideoMuted,
    setMusicStartTime,
    handleSelectAudio,
    handleReplaceAudio,
    handleClearAudio,
    handleToggleVideoMute,
  }
}
